// src/pages/Inviter.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, IconButton,
  Collapse, InputLabel, Tooltip
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule } from '../hooks/useFieldValidation';
import AccountPickerSingle from '../components/AccountPickerSingle';
// ==== Границы полей: 1-в-1 из StartInviterCommand и вложенных конфигов ====
// В модели у числовых полей нет ge/le (только дефолты) — ставим разумные минимумы
// без выдуманного потолка. Исключение: рабочие часы логически ограничены 0..24
// (по описанию поля в WorkingHoursConfig).
const IV_LIMITS: Record<string, FieldLimit> = {
  // AdminInviteConfig.delay_before_demote_sec [min, max]
  delayBeforeDemoteMin: { min: 0, integer: true },
  delayBeforeDemoteMax: { min: 0, integer: true },

  // ProgressiveLimitsConfig
  startInvitesPerDay: { min: 1, integer: true },
  maxInvitesPerDay: { min: 1, integer: true },
  dailyIncrease: { min: 1, integer: true },
  startInvitesPerHour: { min: 1, integer: true },
  maxInvitesPerHour: { min: 1, integer: true },
  hourlyIncrease: { min: 0, step: 0.1 },
  daysToMax: { min: 1, integer: true },

  // WorkingHoursConfig (start/stop, часы 0..24)
  workingHoursStart: { min: 0, max: 24, step: 0.5 },
  workingHoursEnd: { min: 0, max: 24, step: 0.5 },

  // SafetyConfig
  invitesPerAccountHour: { min: 1, integer: true },
  invitesPerAccountDay: { min: 1, integer: true },
  delayBetweenInvitesMin: { min: 0, integer: true },
  delayBetweenInvitesMax: { min: 0, integer: true },
  maxFloodWaitSec: { min: 0, integer: true },
  maxDailyInvitesTotal: { min: 1, integer: true, optional: true },
};

const IV_PAIRS: PairRule[] = [
  { minKey: 'delayBeforeDemoteMin', maxKey: 'delayBeforeDemoteMax' },
  { minKey: 'startInvitesPerDay', maxKey: 'maxInvitesPerDay', message: 'Максимум должен быть ≥ старта' },
  { minKey: 'startInvitesPerHour', maxKey: 'maxInvitesPerHour', message: 'Максимум должен быть ≥ старта' },
  { minKey: 'delayBetweenInvitesMin', maxKey: 'delayBetweenInvitesMax' },
  { minKey: 'workingHoursStart', maxKey: 'workingHoursEnd', message: 'Конец должен быть позже начала' },
];



interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveCampaign {
  campaign_id: string;
  name: string;
  account_names: string[];
  status: string;
  target_chat_name?: string;
  total_users: number;
  invited_count: number;
  skipped_count: number;
  failed_count: number;
  progress_percent: number;
}

const Inviter: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAdminInvite, setShowAdminInvite] = useState(false);
  const [showProgressive, setShowProgressive] = useState(false);

  const [form, setForm] = useState({
    name: '',
    accountNames: [] as string[],
    targetChat: '',
    usersList: '',
    recipientsFile: null as File | null,
    usingAdminInvite: false,
    mainAccountName: '',
    demoteImmediately: true,
    delayBeforeDemoteMin: '1',
    delayBeforeDemoteMax: '3',
    progressiveLimits: false,
    startInvitesPerDay: '3',
    maxInvitesPerDay: '50',
    dailyIncrease: '2',
    startInvitesPerHour: '2',
    maxInvitesPerHour: '20',
    hourlyIncrease: '1.0',
    daysToMax: '14',
    workingHoursEnabled: false,
    workingHoursStart: '9.0',
    workingHoursEnd: '21.0',
    invitesPerAccountHour: '20',
    invitesPerAccountDay: '50',
    delayBetweenInvitesMin: '60',
    delayBetweenInvitesMax: '120',
    skipPremium: false,
    skipBots: true,
    skipRecentlyOnline: false,
    maxFloodWaitSec: '3600',
    rotateOnFlood: true,
    progressiveFloodWait: true,
    autoAddToContacts: true,
    redistributeOnBan: true,
    maxDailyInvitesTotal: '200',
    shuffleUsers: true,
    messageText: '',
    description: '',
    tags: '',
  });

  const update = (key: keyof typeof form, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const validation = useFieldValidation({ limits: IV_LIMITS, pairs: IV_PAIRS });

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 8000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const accRes = await api.get('/accounts');
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
      setAccounts(accRes.data.accounts || []);
      fetchActive();
    } catch (err: any) {
      setMessage({ text: 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };
  const isStartDisabled = !form.name.trim() ||
                        form.accountNames.length === 0 ||
                        !form.targetChat.trim() ||
                        (!form.recipientsFile && !form.usersList.trim());
  const fetchActive = async () => {
    try {
      const res = await api.get('/inviter/list');
      setCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error(err);
    }
  };

  const startCampaign = async () => {
    if (!form.name.trim() || form.accountNames.length === 0 || !form.targetChat.trim()) {
      setMessage({ text: 'Заполните обязательные поля', type: 'error' });
      return;
    }

    if (!form.recipientsFile && !form.usersList.trim()) {
      setMessage({ text: 'Укажите источник пользователей', type: 'error' });
      return;
    }

    if (!validation.validateAll(form)) {
      setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
      return;
    }

    const formData = new FormData();

    formData.append('name', form.name.trim());
    form.accountNames.forEach(name => formData.append('account_names', name));
    formData.append('target_chat_id', form.targetChat.trim());

    if (form.recipientsFile) {
      formData.append('recipients_file', form.recipientsFile);
    }

    if (form.usersList.trim() && !form.recipientsFile) {
      const lines = form.usersList.split('\n').map(s => s.trim()).filter(Boolean);
      lines.forEach(line => formData.append('user_ids', line)); // оставляем @ если есть
    }

    formData.append('shuffle_users', form.shuffleUsers.toString());

    if (form.messageText.trim()) formData.append('message_text', form.messageText.trim());
    if (form.description.trim()) formData.append('description', form.description.trim());
    if (form.tags.trim()) {
      const tags = form.tags.split(',').map(t => t.trim());
      tags.forEach(tag => formData.append('tags', tag));
    }

    if (form.workingHoursEnabled) {
      formData.append('working_hours[start]', form.workingHoursStart);
      formData.append('working_hours[stop]', form.workingHoursEnd);
      formData.append('working_hours[timezone]', 'Europe/Moscow');
    }

    // === SAFETY КАК JSON-СТРОКА ===
    const safetyConfig: any = {
      invites_per_account_hour: parseInt(form.invitesPerAccountHour),
      invites_per_account_day: parseInt(form.invitesPerAccountDay),
      delay_between_invites_sec: [parseInt(form.delayBetweenInvitesMin), parseInt(form.delayBetweenInvitesMax)],
      skip_premium: form.skipPremium,
      skip_bots: form.skipBots,
      skip_recently_online: form.skipRecentlyOnline,
      max_flood_wait_sec: parseInt(form.maxFloodWaitSec),
      rotate_on_flood: form.rotateOnFlood,
      progressive_flood_wait: form.progressiveFloodWait,
      auto_add_to_contacts: form.autoAddToContacts,
      redistribute_on_ban: form.redistributeOnBan,
      max_daily_invites_total: form.maxDailyInvitesTotal ? parseInt(form.maxDailyInvitesTotal) : null,
    };

    if (form.progressiveLimits) {
      safetyConfig.progressive_limits = {
        enabled: true,
        start_invites_per_day: parseInt(form.startInvitesPerDay),
        max_invites_per_day: parseInt(form.maxInvitesPerDay),
        daily_increase: parseInt(form.dailyIncrease),
        start_invites_per_hour: parseInt(form.startInvitesPerHour),
        max_invites_per_hour: parseInt(form.maxInvitesPerHour),
        hourly_increase: parseFloat(form.hourlyIncrease),
        days_to_max: parseInt(form.daysToMax),
      };
    }

    formData.append('safety', JSON.stringify(safetyConfig));

    // === ADMIN_INVITE КАК JSON-СТРОКА ===
    if (form.usingAdminInvite) {
      const adminConfig = {
        enabled: true,
        main_account_name: form.mainAccountName || null,
        demote_immediately: form.demoteImmediately,
        delay_before_demote_sec: [
          parseInt(form.delayBeforeDemoteMin),
          parseInt(form.delayBeforeDemoteMax)
        ]
      };
      formData.append('admin_invite', JSON.stringify(adminConfig));
    }

    setIsLoading(true);
    try {
      await api.post('/inviter/start', formData);
      setMessage({ text: 'Инвайтер запущен!', type: 'success' });
      setTab('active');
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка запуска', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const stopCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/inviter/stop/${id}`);
      setMessage({ text: 'Кампания остановлена', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка остановки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restartCampaign = async (id: string) => {
    setIsLoading(true);
    try {
      await api.get(`/inviter/restart/${id}`);
      setMessage({ text: 'Кампания перезапущена', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка перезапуска', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Удалить кампанию навсегда?')) return;
    setIsLoading(true);
    try {
      await api.get(`/inviter/delete/${id}`);
      setMessage({ text: 'Кампания удалена', type: 'success' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка удаления', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <PersonAddIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Инвайтер
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Инвайтинг в группы" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Прогрессирующие лимиты" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Поддержка админ - инвайта" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматически приглашает пользователей в ваш чат. Поддержка админ-инвайта, совместимость с выгрузками из нашего парсера
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает инвайтер?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Загружаете список пользователей или файл — инвайтер автоматически приглашает их в ваш чат, используя все доступные методы обхода лимитов.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Методы</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Обычный инвайт<br />
              • Админ-инвайт (обход лимитов)
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Безопасность</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Прогрессирующие лимиты<br />
              • Ротация аккаунтов<br />
              • Обработка Floodwait и естественные задержки<br />
              • Фильтры (премиум, боты)
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{color: '#1E40AF', fontWeight: 500, mb: 1}}>Рекомендации</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              • Используйте админ-инвайт<br />
              • Включайте прогрессию<br />
              • Добавляйте 3+ аккаунта и выбирайте отлежавшийся чат<br />
              • Начинайте с небольших выборок
            </Typography>


          </Box>

        </Box>
          <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Направляйте целевой трафик на ваши ресурсы через инвайты в ваши рекламные чаты
        </Typography>
            <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/inviter"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Новая кампания
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои инвайтеры ({campaigns.length})
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {tab === 'setup' && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField label="Название кампании" value={form.name} onChange={e => update('name', e.target.value)} fullWidth required />
              </Grid>

              <Grid size={12}>
                <AccountPickerMulti
                  accounts={accounts}
                  selected={form.accountNames}
                  onChange={(names) => update('accountNames', names)}
                  groups={groups}
                />
              </Grid>

              <Grid size={12}>
                <TextField label="Целевой чат" value={form.targetChat} onChange={e => update('targetChat', e.target.value)} fullWidth required placeholder="@mychat или -100..." />
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Получатели
                  <Tooltip title="Можно ввести вручную (username или номер телефона) или загрузить файл">
                    <InfoIcon fontSize="small" sx={{ ml: 1, color: '#94A3B8' }} />
                  </Tooltip>
                </Typography>

                <TextField
                  label="Username или номер телефона (по одному в строке)"
                  value={form.usersList}
                  onChange={e => update('usersList', e.target.value)}
                  fullWidth
                  disabled={!!form.recipientsFile}
                  multiline
                  rows={8}
                  placeholder="@username&#10;+79991234567&#10;username_without_at"
                  helperText={form.recipientsFile ? 'Файл загружен — ручной ввод отключён' : 'Поддерживается @username, username или номер телефона'}
                />

                <Box sx={{ mt: 2, p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #CBD5E1' }}>
                  <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                    Загрузить файл с получателями (.txt, .xlsx, .csv, .json)
                    <input
                      type="file"
                      hidden
                      accept=".txt,.csv,.json,.xlsx,.parquet"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => e.target.files?.[0] && update('recipientsFile', e.target.files[0])}
                    />
                  </Button>

                  {form.recipientsFile && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon color="primary" />
                      <Typography variant="body2">{form.recipientsFile.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(form.recipientsFile.size / 1024 / 1024).toFixed(2)} МБ)
                      </Typography>
                      <IconButton size="small" onClick={() => update('recipientsFile', null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    В файле должна быть колонка с <strong>username</strong>, <strong>phone</strong>, <strong>telegram_id</strong> или <strong>user_id</strong>
                  </Typography>
                </Box>
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.usingAdminInvite} onChange={e => { update('usingAdminInvite', e.target.checked); setShowAdminInvite(e.target.checked); }} />}
                  label="Админ-инвайт (обход лимитов)"
                />
              </Grid>

              <Collapse in={showAdminInvite}>
                <Box sx={{ pl: 4, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, mb: 3 }}>
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      <AccountPickerSingle
                        accounts={accounts}
                        selected={form.mainAccountName}
                        onChange={(name) => update('mainAccountName', name)}
                        placeholder="Главный аккаунт (выдаёт права)"
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel
                        control={<Checkbox checked={form.demoteImmediately} onChange={e => update('demoteImmediately', e.target.checked)} />}
                        label="Сразу убирать права после инвайта"
                      />
                    </Grid>
                    {!form.demoteImmediately && (
                      <>
                        <Grid size={6}>
                          <TextField
                            label="Задержка мин (сек)"
                            type="number"
                            {...validation.fieldProps('delayBeforeDemoteMin', form, update)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            label="Задержка макс (сек)"
                            type="number"
                            {...validation.fieldProps('delayBeforeDemoteMax', form, update)}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Collapse>

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.progressiveLimits} onChange={e => { update('progressiveLimits', e.target.checked); setShowProgressive(e.target.checked); }} />}
                  label="Прогрессивные лимиты"
                />
              </Grid>

              <Collapse in={showProgressive}>
                <Box sx={{ pl: 4, bgcolor: '#F8FAFC', p: 3, borderRadius: 2, mb: 3 }}>
                  <Grid container spacing={3}>
                    <Grid size={6}>
                      <TextField
                        label="Старт в день"
                        type="number"
                        {...validation.fieldProps('startInvitesPerDay', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Макс в день"
                        type="number"
                        {...validation.fieldProps('maxInvitesPerDay', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Увеличение в день"
                        type="number"
                        {...validation.fieldProps('dailyIncrease', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Старт в час"
                        type="number"
                        {...validation.fieldProps('startInvitesPerHour', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Макс в час"
                        type="number"
                        {...validation.fieldProps('maxInvitesPerHour', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Увеличение в час"
                        type="number"
                        {...validation.fieldProps('hourlyIncrease', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Дней до максимума"
                        type="number"
                        {...validation.fieldProps('daysToMax', form, update)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.workingHoursEnabled} onChange={e => update('workingHoursEnabled', e.target.checked)} />}
                  label="Ограничить рабочие часы"
                />
              </Grid>
              {form.workingHoursEnabled && (
                <>
                  <Grid size={6}>
                    <TextField
                      label="Начало (час)"
                      type="number"
                      {...validation.fieldProps('workingHoursStart', form, update)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      label="Конец (час)"
                      type="number"
                      {...validation.fieldProps('workingHoursEnd', form, update)}
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <Button fullWidth variant="outlined" onClick={() => setShowAdvanced(!showAdvanced)} endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                  Безопасность и задержки
                </Button>
              </Grid>

              <Collapse in={showAdvanced}>
                <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Grid container spacing={3}>
                    <Grid size={6}>
                      <TextField
                        label="Инвайтов в час с аккаунта"
                        type="number"
                        {...validation.fieldProps('invitesPerAccountHour', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Инвайтов в день с аккаунта"
                        type="number"
                        {...validation.fieldProps('invitesPerAccountDay', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Задержка мин (сек)"
                        type="number"
                        {...validation.fieldProps('delayBetweenInvitesMin', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Задержка макс (сек)"
                        type="number"
                        {...validation.fieldProps('delayBetweenInvitesMax', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.skipPremium} onChange={e => update('skipPremium', e.target.checked)} />} label="Пропускать Premium - пользователей" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.skipBots} onChange={e => update('skipBots', e.target.checked)} />} label="Пропускать ботов" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.skipRecentlyOnline} onChange={e => update('skipRecentlyOnline', e.target.checked)} />} label="Пропускать пользователей 'недавно онлайн'" />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Макс FloodWait (сек)"
                        type="number"
                        {...validation.fieldProps('maxFloodWaitSec', form, update)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.rotateOnFlood} onChange={e => update('rotateOnFlood', e.target.checked)} />} label="Ротация при FloodWait" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.progressiveFloodWait} onChange={e => update('progressiveFloodWait', e.target.checked)} />} label="Прогрессивное ожидание FloodWait" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.autoAddToContacts} onChange={e => update('autoAddToContacts', e.target.checked)} />} label="Добавлять приглашаемого в контакты при ошибке" />
                    </Grid>
                    <Grid size={12}>
                      <FormControlLabel control={<Checkbox checked={form.redistributeOnBan} onChange={e => update('redistributeOnBan', e.target.checked)} />} label="Перераспределять приглашаемых при бане аккаунта" />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Макс инвайтов в чат/день"
                        type="number"
                        {...validation.fieldProps('maxDailyInvitesTotal', form, update)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>

              <Grid size={12}>
                <TextField label="Сообщение при инвайте" value={form.messageText} onChange={e => update('messageText', e.target.value)} fullWidth multiline rows={3} />
              </Grid>

              <Grid size={12}>
                <TextField label="Описание" value={form.description} onChange={e => update('description', e.target.value)} fullWidth multiline rows={2} />
              </Grid>

              <Grid size={12}>
                <TextField label="Теги" value={form.tags} onChange={e => update('tags', e.target.value)} fullWidth placeholder="реклама, тест" />
              </Grid>

              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={form.shuffleUsers} onChange={e => update('shuffleUsers', e.target.checked)} />} label="Перемешать пользователей" />
              </Grid>

              <Grid size={12}>
                <Button
                    variant="contained"
                    onClick={startCampaign}
                    disabled={isLoading || isStartDisabled || validation.hasErrors}
                    startIcon={isLoading ? <CircularProgress size={22}/> : <PlayArrowIcon/>}
                    fullWidth
                    sx={{
                      py: 2,
                      fontSize: '1.1rem',
                      bgcolor: '#1E3A8A',
                      '&:hover': {bgcolor: '#1E40AF'}
                    }}
                >
                  {isLoading ? 'Запуск...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {tab === 'active' && (
            campaigns.length === 0 ? (
              <Typography textAlign="center" py={10} color="text.secondary">
                Нет активных инвайтеров
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Название</strong></TableCell>
                      <TableCell><strong>Аккаунты</strong></TableCell>
                      <TableCell><strong>Цель</strong></TableCell>
                      <TableCell><strong>Приглашено</strong></TableCell>
                      <TableCell><strong>Прогресс</strong></TableCell>
                      <TableCell><strong>Статус</strong></TableCell>
                      <TableCell align="right"><strong>Действия</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map(c => {
                      const isRunning = c.status === 'running';
                      const isStopped = c.status === 'stopped';
                      const isCompleted = c.status === 'completed';
                      const isError = c.status === 'error';

                      const statusLabel = isRunning ? 'Работает' :
                                          isStopped ? 'Остановлен' :
                                          isCompleted ? 'Завершён' :
                                          isError ? 'Ошибка' : c.status;

                      const statusColor = isRunning ? 'success' :
                                          isCompleted ? 'success' :
                                              isStopped ? 'warning' :
                                          isError ? 'error' : 'default';


                      return (
                        <TableRow key={c.campaign_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/inviter/detail/${c.campaign_id}`)}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.campaign_id.slice(0, 8)}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>{c.account_names.length}</TableCell>
                          <TableCell>{c.target_chat_name || 'Чат'}</TableCell>
                          <TableCell sx={{ color: '#10B981' }}>{c.invited_count}</TableCell>
                          <TableCell>{c.progress_percent.toFixed(0)}%</TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabel}
                              size="small"
                              color={statusColor}
                              sx={{ minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              {isRunning && (
                                <IconButton size="small" color="error" onClick={() => stopCampaign(c.campaign_id)}>
                                  <StopIcon />
                                </IconButton>
                              )}
                              {isStopped && (
                                <IconButton size="small" color="success" onClick={() => restartCampaign(c.campaign_id)}>
                                  <PlayArrowIcon />
                                </IconButton>
                              )}
                              <IconButton size="small" color="error" onClick={() => deleteCampaign(c.campaign_id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Inviter;